import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Modal, Spin, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { presentationsApi } from '../../../api/presentationsApi';
import {
  IAmenityPick,
  IGalleryCandidate,
  IGalleryPicker,
} from '../../../types/models/presentations';
import {
  ManageGalleriesLink, PhotoPool, PickerBody, PickerFooter, boxWidth, keepOffered, slideOf,
} from './PhotoPool';

/**
 * The amenities slide's captioned photo grid (`image_amenity_N`) of a listing's
 * brochure.
 *
 * Unlike the gallery slots, cells have no default — an empty one stays empty —
 * and each needs a caption per language. The brochure prints them in whole
 * rows, so the screen says how many will print. The grid is a separate set from
 * the gallery slots: a photo may sit in a slide's background and an amenity
 * cell both. Saving sends only `amenity_picks`, which leaves those alone.
 *
 * Same endpoint as `GalleryPicksModal`; its own screen because it is its own
 * checklist step, and because it is the one that needs writing. Uploads go to
 * the same place too: the listing's Brochure gallery.
 *
 * Trigger-as-children, same as `GalleryPicksModal`.
 */

const Body = styled(PickerBody)`
  .amenity-grid {
    display: grid;
    grid-template-columns: repeat(3, max-content);
    gap: 12px 12px;
    margin-bottom: 8px;
  }

  .cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .cell .ant-input {
    font-size: 12px;
  }

  .row-note {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    margin-bottom: 24px;
  }
`;

const samePicks = (
  a: Record<string, IAmenityPick>,
  b: Record<string, IAmenityPick>,
) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return Array.from(keys).every((key) => JSON.stringify(a[key]) === JSON.stringify(b[key]));
};

/** Put `ref` in `slot`; one already in another cell swaps, captions included. */
export const assignAmenityRef = (
  picks: Record<string, IAmenityPick>,
  slot: string,
  ref: string,
): Record<string, IAmenityPick> => {
  const next = { ...picks };
  const holder = Object.keys(picks).find((key) => key !== slot && picks[key].ref === ref);
  if (holder) {
    if (picks[slot]) next[holder] = picks[slot];
    else delete next[holder];
    next[slot] = picks[holder];
  } else {
    next[slot] = { ref, captions: picks[slot]?.captions ?? {} };
  }
  return next;
};

/**
 * How many cells print in `language` — the server's `gallery.amenity_cells`: a
 * cell needs a photo and a caption, gaps close up, and only whole rows print.
 */
export const printedCells = (
  picks: Record<string, IAmenityPick>,
  language: string,
  row: number,
  drawn: number,
) => {
  const ready = Object.values(picks)
    .filter((pick) => pick.ref && (pick.captions[language] ?? '').trim()).length;
  const capped = Math.min(ready, drawn);
  return { ready, printed: capped - (capped % row) };
};

export interface AmenityPhotosEditorProps {
  listingId: number;
  listingName?: string;
  /** For "Manage galleries"; without it the link is not offered. */
  listingSlug?: string;
  onSaved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** In a modal: offered as Close, and called after a save. */
  onClose?: () => void;
}

export interface AmenityPhotosModalProps
  extends Omit<AmenityPhotosEditorProps, 'onClose' | 'onDirtyChange'> {
  children?: ReactNode;
}

/** The grid, its captions and the pool, with their own Save. Loads when mounted. */
export const AmenityPhotosEditor = ({
  listingId, listingName, listingSlug, onSaved, onDirtyChange, onClose,
}: AmenityPhotosEditorProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<IGalleryPicker>();
  const [picks, setPicks] = useState<Record<string, IAmenityPick>>({});
  const [selected, setSelected] = useState<string>();

  useEffect(() => {
    setLoading(true);
    setError(undefined);
    presentationsApi.galleryPicker(listingId)
      .then((result) => {
        setData(result);
        setPicks(result.amenity_picks ?? {});
        setSelected(result.amenity_slots?.[0]?.slot);
      })
      .catch((e: any) => setError(e?.message ?? 'Could not load the gallery.'))
      .finally(() => setLoading(false));
  }, [listingId]);

  const [uploading, setUploading] = useState(false);

  /** The pool again, after the gallery editor; unsaved picks are kept. */
  const reload = useCallback(() => {
    presentationsApi.galleryPicker(listingId)
      .then((result) => {
        setData(result);
        setPicks((current) => keepOffered(current, result.candidates, (pick) => pick.ref));
      })
      .catch((e: any) => setError(e?.message ?? 'Could not load the gallery.'));
  }, [listingId]);

  const upload = async (files: File[]) => {
    setUploading(true);
    setError(undefined);
    try {
      const result = await presentationsApi.uploadGalleryPhotos(listingId, files);
      setData(result);
      const [first] = result.uploaded;
      if (selected && first) setPicks((current) => assignAmenityRef(current, selected, first));
    } catch (e: any) {
      setError(e?.response?.data?.files ?? e?.message ?? 'Could not upload.');
    } finally {
      setUploading(false);
    }
  };

  const byRef = useMemo(() => {
    const map: Record<string, IGalleryCandidate> = {};
    data?.candidates.forEach((candidate) => { map[candidate.ref] = candidate; });
    return map;
  }, [data]);

  const slotOfRef = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(picks).forEach(([slot, pick]) => { map[pick.ref] = slot; });
    return map;
  }, [picks]);

  const slots = data?.amenity_slots ?? [];
  const dirty = !!data && !samePicks(picks, data.amenity_picks ?? {});
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const setCaption = (slot: string, language: string, text: string) => {
    const pick = picks[slot];
    if (!pick) return;
    setPicks({ ...picks, [slot]: { ...pick, captions: { ...pick.captions, [language]: text } } });
  };

  const clear = (slot: string) => {
    const next = { ...picks };
    delete next[slot];
    setPicks(next);
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      // Every cell that was or is filled, with null for the emptied ones, so the
      // server's whole-set replacement cannot keep one we removed. No `picks`:
      // the gallery slots are `GalleryPicksModal`'s and stay as they are.
      const body: Record<string, IAmenityPick | null> = {};
      Object.keys({ ...data.amenity_picks, ...picks }).forEach((slot) => {
        body[slot] = picks[slot] ?? null;
      });
      await presentationsApi.saveAmenityPicks(listingId, body);
      setData({ ...data, amenity_picks: { ...picks } });
      onSaved?.();
      onClose?.();
    } catch (e: any) {
      setError(e?.response?.data?.picks ?? e?.message ?? 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const slide = slots.length ? slideOf(slots[0]) : null;

  return (
    <>
        <Body>
          {error && <Alert type="error" message={String(error)} style={{ marginBottom: 16 }} />}
          {loading && <Spin />}

          {data && slots.length === 0 && (
            <Alert
              type="info"
              showIcon
              message="None of this property's templates has an amenities slide"
            />
          )}

          {data && slots.length > 0 && (
            <>
              <div className="slide-label">
                Amenities slide{slide ? ` (slide ${slide})` : ''}
              </div>
              <div className="amenity-grid">
                {slots.map((slot) => {
                  const pick = picks[slot.slot];
                  const photo = pick ? byRef[pick.ref] : undefined;
                  return (
                    <div className="cell" key={slot.slot}>
                      <button
                        type="button"
                        aria-label={slot.slot}
                        className={['box', selected === slot.slot ? 'selected' : ''].join(' ')}
                        style={{ width: boxWidth(slot) }}
                        onClick={() => setSelected(slot.slot)}
                      >
                        {photo && <img src={photo.thumbnail} alt="" />}
                        <span className="slot-name">{slot.slot}</span>
                        {pick && (
                          <Tooltip title="Empty this cell">
                            <span
                              className="clear"
                              aria-label={`clear ${slot.slot}`}
                              onClick={(e) => { e.stopPropagation(); clear(slot.slot); }}
                            >
                              <CloseOutlined />
                            </span>
                          </Tooltip>
                        )}
                      </button>
                      {data.languages.map((language) => (
                        <Input
                          key={language}
                          size="small"
                          aria-label={`${slot.slot} caption ${language}`}
                          addonBefore={language.toUpperCase()}
                          placeholder={pick ? 'Caption' : 'Pick a photo first'}
                          disabled={!pick}
                          maxLength={data.caption_max}
                          value={pick?.captions[language] ?? ''}
                          onChange={(e) => setCaption(slot.slot, language, e.target.value)}
                          style={{ width: boxWidth(slot) }}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
              <div className="row-note">
                {data.languages.map((language) => {
                  const { ready, printed } = printedCells(
                    picks, language, data.amenity_row, slots.length,
                  );
                  return (
                    <div key={language}>
                      {language.toUpperCase()}: {ready} with photo and caption, prints {printed}
                      {printed === 0
                        ? ` — needs ${data.amenity_row} before the brochure can be generated`
                        : ''}
                    </div>
                  );
                })}
                Rows print whole: {data.amenity_row} photos make a row.
              </div>

              <PhotoPool
                candidates={data.candidates}
                selected={selected}
                slotOf={(ref) => slotOfRef[ref]}
                onChoose={(ref) => selected && setPicks(assignAmenityRef(picks, selected, ref))}
                onUpload={upload}
                uploading={uploading}
                manage={listingSlug && (
                  <ManageGalleriesLink recordPath="listings" recordId={listingSlug}
                                       recordName={listingName ?? 'Property'} onClose={reload} />
                )}
              />
            </>
          )}

          {dirty && (
            <Alert
              style={{ marginTop: 16 }}
              type="info"
              showIcon
              message="Unsaved — the brochure keeps its current photos until you save and re-render."
            />
          )}
        </Body>
        <PickerFooter>
          {onClose && <Button onClick={onClose}>Close</Button>}
          <Tooltip title={dirty ? '' : 'Nothing changed yet.'}>
            <Button type="primary" onClick={save} loading={saving} disabled={!dirty}>
              {onClose ? 'Save' : 'Save photos'}
            </Button>
          </Tooltip>
        </PickerFooter>
    </>
  );
};

const AmenityPhotosModal = ({ children, listingName, ...editor }: AmenityPhotosModalProps) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <span onClick={() => setOpen(true)}>{children}</span>
      <Modal
        visible={open}
        onCancel={() => setOpen(false)}
        title={`Amenity photos${listingName ? ` — ${listingName}` : ''}`}
        width={960}
        destroyOnClose
        footer={null}
      >
        {open && (
          <AmenityPhotosEditor {...editor} listingName={listingName}
                               onClose={() => setOpen(false)} />
        )}
      </Modal>
    </>
  );
};

export default AmenityPhotosModal;
